import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown } from 'lucide-react';
import { useAllMaterials } from '../../hooks';
import { CompareItemSkeleton } from './ChartSkeletons';
import { withPerUnit } from '../../utils/formatUnit';

const CompareDropdown = ({ placeholder = "Compare to...", onItemSelect, maxReached = false, existingComparisons = [], currentMaterialCode = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Fetch all materials from API
  const { data: materials, loading } = useAllMaterials({
    enabled: true,
    lightweight: true,
    cache: true,
    cacheTTL: 5 * 60 * 1000, // Cache for 5 minutes
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter materials excluding current material and existing comparisons
  const filteredMaterials = materials
    ? materials.filter(material => {
        // Exclude current material being viewed
        if (currentMaterialCode && material.code === currentMaterialCode) {
          return false;
        }

        // Exclude materials already in comparisons
        const isDuplicate = existingComparisons.some(comp => comp.code === material.code);
        if (isDuplicate) return false;

        // Apply search filter
        return material.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
               material.code.toLowerCase().includes(searchTerm.toLowerCase());
      })
    : [];

  const handleItemClick = (material) => {
    // Check if max reached
    if (maxReached) return;

    // Call the parent callback with material data
    if (onItemSelect) {
      onItemSelect({
        code: material.code,
        name: material.name,
        price: material.price || withPerUnit(material.last_value ?? '—', material.currency, material.value_unit),
        id: material.id
      });
    }

    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="compare-dropdown-container" ref={dropdownRef}>
      <div
        className={`chart-search-container ${isOpen ? 'active' : ''}`}
        onClick={() => !maxReached && setIsOpen(true)}
      >
        <Search size={16} style={{ color: '#9CA3AF' }} />
        <input
          type="text"
          placeholder={maxReached ? "Maximum comparisons reached" : placeholder}
          className="chart-search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => !maxReached && setIsOpen(true)}
          disabled={maxReached}
        />
        <ChevronDown
          size={16}
          style={{
            color: '#9CA3AF',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}
        />
      </div>

      {isOpen && (
        <div className="compare-dropdown-menu">
          {/* Items List */}
          <div className="compare-dropdown-list">
            {loading ? (
              <div className="compare-dropdown-empty">
                <div className="d-flex flex-column gap-2 w-100">
                  <CompareItemSkeleton />
                  <CompareItemSkeleton />
                  <CompareItemSkeleton />
                  <div className="small text-white-50 mt-1">Loading materials...</div>
                </div>
              </div>
            ) : filteredMaterials.length > 0 ? (
              filteredMaterials.map((material) => (
                <div
                  key={material.code}
                  className="compare-dropdown-item"
                  onClick={() => handleItemClick(material)}
                >
                  <div className="compare-item-name">
                    {material.name}
                    <span style={{ color: '#9CA3AF', fontSize: '12px', marginLeft: '8px' }}>
                      ({material.code})
                    </span>
                  </div>
                  <div className="compare-item-price">{material.price}</div>
                </div>
              ))
            ) : (
              <div className="compare-dropdown-empty">
                {searchTerm
                  ? `No materials found matching "${searchTerm}"`
                  : 'No materials available'
                }
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompareDropdown;
